import { ApolloClient } from 'apollo-client';
import { InMemoryCache, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import queryString from 'query-string';
import uuidv4 from 'uuid/v4';
import { HttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { setRequestId } from 'utils/requestId';
import { BOOKING_ENGINE_UNAUTHORIZED } from 'utils/constants';

export default function configureApollo(history) {
  const params = queryString.parse(history.location.search);
  const token = params.token;
  const requestId = uuidv4();

  setRequestId(requestId);

  const httpLink = new HttpLink({ uri: process.env.REACT_APP_GRAPHQL_API_URI });

  const customHeadersLink = setContext((_, { headers }) => {
    let newHeaders = { ...headers };

    newHeaders['X-WCC-Request-Id'] = requestId;

    // get the authentication token from local storage if it exists
    if (token) {
      newHeaders['authorization'] = `Bearer ${token}`;
    }

    // return the headers to the context so httpLink can read them
    return {
      headers: newHeaders
    };
  });

  const errorLink = onError(({ networkError }) => {
    if (networkError) {
      // 401 UNAUTHORIZED indicates session token is expired
      if (networkError.statusCode === 401) {
        window.parent.postMessage(BOOKING_ENGINE_UNAUTHORIZED, '*');
      }
    }
  });

  const combinedHttpLink = customHeadersLink.concat(errorLink.concat(httpLink));

  const cache = new InMemoryCache({
    dataIdFromObject: object => {
      switch (object.__typename) {
        // Set id to block code if the blockOrCorporateCode turned out to be a block code.
        // This ties in with a cacheRedirect below for the blockOrCorporateCodeRates query.
        case 'BlockOrCorporateCodeRates':
          return object.blockCode
            ? `${object.__typename}:${object.propertyCode}_${object.blockCode.code}`
            : defaultDataIdFromObject(object);
        case 'BlockCode':
          return object.code;
        default:
          return defaultDataIdFromObject(object); // fall back to `id` and `_id` for all other types
      }
    },
    cacheRedirects: {
      Query: {
        // Re-use data from cache when querying a block code that was already loaded, since start/end dates of query
        // do not matter when it's a block code.
        blockOrCorporateCodeRates: (_, { input }, { getCacheKey }) =>
          getCacheKey({
            __typename: 'BlockOrCorporateCodeRates',
            propertyCode: input.propertyCode,
            blockCode: { code: input.blockOrCorporateCode }
          })
      },
      BlockOrCorporateCodeRates: {
        propertyCode: (_, { input }) => input.propertyCode
      }
    }
  });

  const client = new ApolloClient({
    link: combinedHttpLink,
    cache
  });

  return client;
}
